import React, { Component, ReactNode, RefObject } from 'react';
import { get } from 'lodash';
import * as R from 'remeda';
import { withTheme } from '@emotion/react';
import clsx from 'clsx';
import { DispatchProp } from 'react-redux';
import { Subject } from 'rxjs';
import { DatapointAutomationBlocker } from '@rossum/api-client/automationBlockers';
import { isSchemaDatapoint } from '@rossum/api-client/schemas';
import { Message } from '@rossum/api-client/shared';
import { Box, Stack, Theme } from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import { isEmbedded } from '../../constants/config';
import SelectInputFooter from '../../containers/SelectInputFooter';
import { getFromUnion } from '../../lib/helpers';
import {
  selectDatapoint,
  updateDatapointValue,
} from '../../redux/modules/datapoints/actions';
import { updateSuggestedOperationPositionAction } from '../../redux/modules/datapoints/suggestedOperations/actions';
import { getBorderColor } from '../../redux/modules/datapoints/suggestedOperations/utils';
import { isVirtualDatapoint } from '../../redux/modules/datapoints/typedHelpers';
import {
  getUIEditabilityFromSchema,
  getUITypeFromSchema,
  isFieldEditable,
} from '../../redux/modules/schema/helpers';
import { startEditingDatapointValue } from '../../redux/modules/ui/actions';
import { updateUiSettings } from '../../redux/modules/user/actions';
import {
  AnyDatapointDataST,
  MatchedTriggerRuleDatapoint,
  SimpleDatapointDataST,
  SuggestedOperationWithMeta,
} from '../../types/datapoints';
import { AnyDatapointSchema, ButtonDatapointSchema } from '../../types/schema';
import { AnnotationValidation } from '../../types/user';
import FooterCell from '../footerCell';
import ButtonDatapoint from './ButtonDatapoint';
import DatapointMessage from './components/DatapointMessage';
import DragHandler from './components/DragHandler';
import { OnValueChangeOptions } from './components/Highlighter';
import Input from './components/Input';
import SoftWarning from './components/SoftWarning';
import UiFieldTypeMessage from './components/UiFieldTypeMessage';
import DatapointTooltip, {
  getTooltipTypeWithHighestPriority,
} from './DatapointTooltip';
import {
  getLoggableDatapointValue,
  isDatapointEditedFormulaField,
  isFieldEditableFormula,
  validationTickColor,
} from './helpers';
import styles from './style.module.sass';

export const scrollToDatapoint = new Subject();
export type LastInteractionT = 'scroll' | 'navigation';

export type OwnProps = {
  automationBlockers?: DatapointAutomationBlocker[];
  blockersVisible?: boolean;
  matchedRules?: MatchedTriggerRuleDatapoint;
  childSelected?: boolean;
  currentColumn?: string;
  datapointIndex?: number;
  documentAutomated: boolean;
  editingDatapointValue: boolean;
  endDrag?: () => void;
  hideDragger?: boolean;
  inFooter: boolean;
  index?: number;
  isLoading?: boolean;
  isSimpleMultivalue: boolean;
  message: Message | null | undefined;
  myPath: Array<number>;
  readOnly: boolean;
  resizing?: boolean;
  schema?: AnyDatapointSchema;
  searchPanelShown: boolean;
  setRef?: RefObject<HTMLDivElement>;
  startDrag?: () => void;
  suggestedOperation?: SuggestedOperationWithMeta;
  suggestedOperationsSchemaIds?: string[];
  lastInteraction?: LastInteractionT;
  handleLastInteraction?: (interactionAction: LastInteractionT) => void;
  hideCapturedSoftWarning?: boolean;
  widthOverride?: number;
  hideDataSoftWarning?: boolean;
  formulaValidationInProgress?: boolean;
  isGhostRow?: boolean;
  active: boolean;
  data: AnyDatapointDataST;
  annotationValidationMode: AnnotationValidation | undefined;
};

type Props = OwnProps &
  DispatchProp & {
    // TODO: Refactor when select is rewritten to MUI
    theme: Theme;
  };

const Div = ({
  children,
  className,
}: {
  children?: ReactNode;
  className?: string;
}) => {
  return <div className={className}>{children}</div>;
};

const StickyDiv = (
  props: {
    sticky: boolean;
    isSimpleMultivalue: boolean;
    className: string;
    isActive: boolean;
  } & React.Attributes
) => {
  const { sticky } = props;

  return React.cloneElement(<Div />, {
    ...props,
    className: clsx(
      props.className,
      !props.isSimpleMultivalue && styles['ActiveWrapper'],
      sticky && styles['StickyWrapper']
    ),
  });
};

class Datapoint extends Component<Props> {
  input: HTMLInputElement | null = null;

  justClicked = false;

  sticky = false;

  // oxlint-disable-next-line react/no-unsafe
  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const becameActive = nextProps.active && !this.props.active;

    // A newly activated datapoint should never be sticky,
    // because the sidebar will be scrollled to show it.
    if (becameActive) {
      this.sticky = false;
      return;
    }

    // Whenever the user starts scrolling,
    // the datapoint switches to sticky, and stays like that.
    if (
      nextProps.lastInteraction === 'scroll' &&
      this.props.lastInteraction !== 'scroll'
    ) {
      this.sticky = true;
    }
  }

  componentDidUpdate({ searchPanelShown, active: prevActive }: Props) {
    const { active, inFooter } = this.props;

    const focusOnSearchPanelClose = [
      !this.props.searchPanelShown,
      searchPanelShown,
      active,
    ].every(R.isTruthy);

    if (!inFooter && active && !prevActive) {
      scrollToDatapoint.next();
    }

    if (focusOnSearchPanelClose && this.input) {
      // This does not seem to do anything anyway
      this.input.focus();
    }
  }

  onValueChange = (value: string, options?: OnValueChangeOptions) => {
    const { data } = this.props;

    if (data && !!this.props.suggestedOperation) {
      this.props.dispatch(
        updateSuggestedOperationPositionAction(data.id, {
          content: {
            value,
          },
        })
      );

      return;
    }

    if (data)
      this.props.dispatch(
        updateDatapointValue(
          {
            id: data.id,
            index: data.meta.index,
            validationSource: options?.setHumanValidationSource ? 'human' : '',
            struct: options?.struct,
            oldValue: getLoggableDatapointValue(data),
            reason: isFieldEditableFormula(this.props.schema)
              ? 'manual-formula-override-sidebar'
              : 'input-sidebar',
            noRecalculation:
              isFieldEditableFormula(this.props.schema) || undefined,
          },
          value
        )
      );
  };

  handleDoubleClick = () => {
    if (this.justClicked && !this.props.readOnly) {
      this.justClicked = false;
      this.props.dispatch(startEditingDatapointValue());
    } else {
      this.justClicked = true;
      setTimeout(() => {
        this.justClicked = false;
      }, 500);
    }
  };

  onClick = () => {
    const { schema, active, myPath, handleLastInteraction, childSelected } =
      this.props;

    if (get(schema, 'type') !== 'button') {
      this.handleDoubleClick();
      if (!active || childSelected) {
        this.props.dispatch(selectDatapoint(myPath, { noTail: true }));

        if (handleLastInteraction) {
          handleLastInteraction('navigation');
        }
      }
    }
  };

  isOptionalSelect = (options: { value: string; label: string }[]) => {
    const constraints = get(this.props.schema, 'constraints');
    const hasEmptyValue = options.some(o => o.value === '');

    return !hasEmptyValue && !(constraints?.required ?? true);
  };

  render() {
    const {
      active,
      automationBlockers,
      childSelected,
      currentColumn,
      data,
      documentAutomated,
      editingDatapointValue,
      endDrag,
      hideDragger,
      inFooter,
      isSimpleMultivalue,
      matchedRules,
      message,
      readOnly,
      resizing,
      schema,
      setRef,
      startDrag,
      suggestedOperation,
      suggestedOperationsSchemaIds,
      theme,
      isLoading,
      formulaValidationInProgress,
      dispatch,
      hideCapturedSoftWarning,
      hideDataSoftWarning,
      isGhostRow,
      widthOverride,
      blockersVisible,
      annotationValidationMode,
    } = this.props;

    const uiFieldType = getUITypeFromSchema(schema);
    const isEditable = isFieldEditable(schema);
    const editability = getUIEditabilityFromSchema(schema);
    const isEditedFormula = isDatapointEditedFormulaField(data, schema);

    const isSelect = get(schema, 'type') === 'enum';
    const isButton = get(schema, 'type') === 'button';
    // Removed check for position because you can still edit cell in footer if it has a bbox, the border just didn't show
    const isEditingValue = inFooter && active && editingDatapointValue;

    const isDatapointFromAddOperation =
      isVirtualDatapoint(data.id) &&
      !!(data as SimpleDatapointDataST).content?.value &&
      !isGhostRow;

    const operationSource = isDatapointFromAddOperation
      ? ('table' as const)
      : suggestedOperation
        ? suggestedOperation.source
        : undefined;

    const classNames = clsx(
      styles['FooterDatapoint'],
      active && styles['ActiveHighlight'],
      isSelect &&
        !inFooter &&
        !(readOnly || !isEditable) &&
        clsx(
          styles['SelectSidebarDatapoint'],
          active && styles['SelectSidebarDatapointActive']
        ),
      operationSource &&
        (operationSource === 'table'
          ? styles['SuggestedV2']
          : styles['Suggested']),

      'Datapoint'
    );

    const displayAutomationBlockers = !!automationBlockers && !!blockersVisible;

    const tickIconColor = validationTickColor(
      data,
      active,
      !!suggestedOperation,
      message?.type === 'error'
    );

    const borderColor =
      operationSource === 'table'
        ? theme.palette.aurora.main
        : getBorderColor(
            suggestedOperation?.meta.datapoint.schemaId,
            suggestedOperationsSchemaIds
          );

    const style = {
      borderColor,
    };

    const Wrapper = inFooter ? FooterCell : active ? StickyDiv : Div;
    const columnSchemaId = get(schema, 'id');
    const displayDragHandler = !hideDragger && inFooter && active && !readOnly;

    const options =
      getFromUnion(data, 'options') || getFromUnion(schema, 'options') || [];

    const isClearable = this.isOptionalSelect(options);

    const simpleMultivalueChildIsSelected = isSimpleMultivalue && childSelected;

    const softWarningCanBeShown = !inFooter && !readOnly;

    if (!data || !schema || R.isEmpty(schema)) {
      return null;
    }

    return (
      <Wrapper
        width={get(schema, 'width')}
        label={get(schema, 'label')}
        type={get(schema, 'type')}
        stretch={get(schema, 'stretch')}
        className="sectionWrapper"
        isSimpleMultivalue={active && isSimpleMultivalue}
        isActive={active}
        sticky={this.sticky}
        uiFieldType={uiFieldType ?? null}
        widthOverride={widthOverride}
      >
        <Box
          id={
            active && !inFooter && data.category !== 'multivalue'
              ? 'magic-line-source'
              : undefined
          }
          sx={{
            color:
              operationSource === 'table'
                ? t => t.palette.text.secondary
                : undefined,
            borderRadius: active && inFooter ? borderRadius('s') : undefined,
            border: t =>
              isEditingValue
                ? `2px solid ${t.palette.primary.main}`
                : undefined,
            backgroundColor: t => (active ? t.palette.action.focus : undefined),
          }}
          data-cy="annotation-sidebar-datapoint"
          data-sa-extension-schema-id={data.schemaId}
          ref={simpleMultivalueChildIsSelected ? null : setRef}
          style={style}
          className={classNames}
          onClick={this.onClick}
        >
          <DatapointMessage
            hideBorder={isEditingValue || !!suggestedOperation}
            isButton={isButton}
            message={message ?? undefined}
            active={active}
            resizing={resizing}
            inFooter={inFooter}
          >
            {({ messageIcon: _messageIcon }) => (
              <UiFieldTypeMessage
                uiFieldType={inFooter || isEditedFormula ? null : uiFieldType}
                readOnly={readOnly}
                fieldEditable={isEditable}
              >
                {(() => {
                  switch (data.category) {
                    case 'multivalue':
                      // we use Datapoint component only in Footer and we do not support multivalues nested in multivalues
                      // so it's safe to return null
                      return null;
                    case 'datapoint': {
                      switch (get(schema, 'type')) {
                        case 'button':
                          return (
                            <ButtonDatapoint
                              schema={schema as ButtonDatapointSchema}
                              data={data}
                              inFooter={inFooter}
                              annotationValidationMode={
                                annotationValidationMode
                              }
                            />
                          );
                        case 'enum': {
                          // If value of enum is not found, it has value === '', but there could be
                          // an option with empty string value in the options as well.
                          // We want to set this option instead of fallbacking to default value if the empty value option is available
                          const isEmptyStringInOptions = options.some(
                            option => option.value === ''
                          );

                          const isRequired = isSchemaDatapoint(data.schema)
                            ? (data.schema?.constraints?.required ?? true)
                            : true;

                          const value = R.find(options, option => {
                            const resolvedValue =
                              data.content?.value !== undefined &&
                              data.content.value !== null &&
                              (data.content.value !== '' ||
                                isEmptyStringInOptions ||
                                !isRequired)
                                ? data.content.value
                                : (data.schema?.defaultValue ?? '');

                            return typeof option.value === 'string'
                              ? R.toLowerCase(option.value) ===
                                  R.toLowerCase(resolvedValue)
                              : option.value === resolvedValue;
                          });

                          return (
                            <>
                              {inFooter && (
                                <DatapointTooltip
                                  data={data}
                                  active={active}
                                  automationBlockers={automationBlockers}
                                  matchedRules={matchedRules}
                                  tickIconColor={tickIconColor}
                                  documentAutomated={documentAutomated}
                                  inFooter
                                  tooltipType={getTooltipTypeWithHighestPriority(
                                    {
                                      displayAutomationBlockers,
                                      tickIconColor,
                                      uiFieldType,
                                    }
                                  )}
                                />
                              )}

                              <SelectInputFooter
                                clearable={isClearable}
                                readOnly={readOnly || !isEditable}
                                onChange={this.onValueChange}
                                options={options}
                                style={
                                  matchedRules
                                    ? {
                                        color: theme.palette.warning.main,
                                      }
                                    : undefined
                                }
                                active={active}
                                value={value}
                              />
                            </>
                          );
                        }
                        default:
                          return (
                            <Input
                              isLoading={
                                isLoading || formulaValidationInProgress
                              }
                              inFooter={inFooter}
                              active={active}
                              data={{
                                value: '',
                                page: null,
                                position: null,
                                ...data.content,
                                ...get(
                                  suggestedOperation,
                                  ['value', 'content'],
                                  {}
                                ),
                              }}
                              displayAutomationBlockers={
                                displayAutomationBlockers
                              }
                              documentAutomated={documentAutomated}
                              automationBlockers={automationBlockers}
                              columnSchemaId={columnSchemaId}
                              editingDatapointValue={editingDatapointValue}
                              onChange={this.onValueChange}
                              currentColumn={currentColumn}
                              readOnly={readOnly}
                              setRef={(ref: HTMLInputElement | null) => {
                                this.input = ref;
                              }}
                              schema={schema}
                              tickIconColor={tickIconColor}
                              isEditable={isEditable}
                              datapointData={data}
                            />
                          );
                      }
                    }
                    default:
                      return null;
                  }
                })()}
              </UiFieldTypeMessage>
            )}
          </DatapointMessage>
          {displayDragHandler && startDrag && endDrag && (
            <DragHandler startDrag={startDrag} endDrag={endDrag} />
          )}
        </Box>
        <Stack sx={{ ':not(:empty)': { mb: 1 } }}>
          {((uiFieldType === 'captured' &&
            !hideCapturedSoftWarning &&
            !isSelect &&
            editability !== 'enabled_without_warning') ||
            (uiFieldType === 'data' &&
              !hideDataSoftWarning &&
              editability === 'enabled')) && (
            <SoftWarning
              active={active && !isEmbedded() && softWarningCanBeShown}
              textKey={`components.datapoint.warning.edit.text.${uiFieldType}`}
              buttonKey="components.datapoint.warning.edit.button"
              textKeySecondary={
                uiFieldType === 'captured'
                  ? 'components.datapoint.warning.edit.text.captured.shortcut'
                  : undefined
              }
              handleOnClick={() =>
                dispatch(
                  updateUiSettings({
                    hideWarningEditableFields: {
                      [uiFieldType]: true,
                    },
                  })
                )
              }
              dataCy={`datapoint-hide-warning-editable-${uiFieldType}-fields`}
            />
          )}
        </Stack>
      </Wrapper>
    );
  }
}

export default withTheme(Datapoint);
